import type { FlowStep } from "@/common/flow/flow-step.types";
import { getString } from "@/common/uistringlabels/ui-string-utils";
import type { ReplacementDraft } from "./replacement.types";
import { PHONE_NUMBER_PATTERN } from "./steps/ChooseDelivery/components/phone-number-fields";

const DELIVERY_ERRORS = "replacement.chooseDelivery.errors";

const isChooseDeviceComplete = (draft: ReplacementDraft): boolean =>
  Boolean(draft.deviceId && draft.keyboardLayout);

const hasUsablePickupPhone = (draft: ReplacementDraft): boolean =>
  Boolean(draft.pickupPhone?.phoneCountryCode.trim()) &&
  PHONE_NUMBER_PATTERN.test(draft.pickupPhone?.phoneNumber.trim() ?? "");

const firstDeliveryGap = (draft: ReplacementDraft): string | undefined => {
  if (!draft.deliveryMethod) return getString(`${DELIVERY_ERRORS}.methodRequired`);
  if (draft.deliveryMethod === "ship") {
    if (!draft.shippingAddress) return getString(`${DELIVERY_ERRORS}.shippingAddressRequired`);
    return draft.isEditing ? getString(`${DELIVERY_ERRORS}.unsavedAddress`) : undefined;
  }
  if (!draft.pickup) return getString(`${DELIVERY_ERRORS}.buildingRequired`);
  return hasUsablePickupPhone(draft) ? undefined : getString(`${DELIVERY_ERRORS}.pickupPhoneRequired`);
};

export const chooseDeviceStep: FlowStep<ReplacementDraft> = {
  id: "choose-device",
  label: "Choose device",
  isReachable: () => true,
  validate: (draft) =>
    isChooseDeviceComplete(draft)
      ? undefined
      : "Choose a device and a keyboard layout to continue.",
};

export const chooseDeliveryStep: FlowStep<ReplacementDraft> = {
  id: "choose-delivery",
  label: "Choose delivery",
  isReachable: isChooseDeviceComplete,
  validate: firstDeliveryGap,
};

export const reviewStep: FlowStep<ReplacementDraft> = {
  id: "review",
  label: "Review details",
  isReachable: (draft) => isChooseDeviceComplete(draft) && !firstDeliveryGap(draft),
  validate: () => undefined,
};

export const replacementSteps: FlowStep<ReplacementDraft>[] = [
  chooseDeviceStep,
  chooseDeliveryStep,
  reviewStep,
];
